"use client"

import { t } from "@/languages/i18n"

interface LatestNewsPaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  loading?: boolean
}

const LatestNewsPagination = ({
  currentPage,
  totalPages,
  onPageChange,
  loading = false,
}: LatestNewsPaginationProps) => {
  const renderPaginationButtons = () => {
    const buttons = []

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        buttons.push(
          <button
            key={i}
            onClick={() => onPageChange(i)}
            disabled={loading}
            className={`w-9 h-9 flex rounded text-black items-center justify-center text-base font-normal transition-all duration-300 ${
              currentPage === i ? "bg-[#37b24d] text-white" : "bg-white border border-[#ced4da]"
            }`}
            aria-label={`${t("Page")} ${i}`}
          >
            {i}
          </button>,
        )
      }
      return buttons
    }

    buttons.push(
      <button
        key={1}
        onClick={() => onPageChange(1)}
        disabled={loading}
        className={`w-9 h-9 flex rounded text-black items-center justify-center text-base font-normal transition-all duration-300 ${
          currentPage === 1 ? "bg-[#37b24d] text-white" : "bg-white border border-[#ced4da]"
        }`}
        aria-label={`${t("Page")} 1`}
      >
        1
      </button>,
    )

    if (currentPage <= 4) {
      const maxPage = Math.min(5, totalPages)
      for (let i = 2; i <= maxPage; i++) {
        buttons.push(
          <button
            key={i}
            onClick={() => onPageChange(i)}
            disabled={loading}
            className={`w-9 h-9 flex rounded text-black items-center justify-center text-base font-normal transition-all duration-300 ${
              currentPage === i ? "bg-[#37b24d] text-white" : "bg-white border border-[#ced4da]"
            }`}
            aria-label={`${t("Page")} ${i}`}
          >
            {i}
          </button>,
        )
      }
      if (totalPages > 5) {
        buttons.push(
          <span
            key="ellipsis"
            className="w-9 h-9 flex rounded text-black items-center justify-center text-base font-normal"
          >
            ...
          </span>,
        )
        buttons.push(
          <button
            key={totalPages}
            onClick={() => onPageChange(totalPages)}
            disabled={loading}
            className={`w-9 h-9 flex rounded text-black items-center justify-center text-base font-normal transition-all duration-300 ${
              currentPage === totalPages ? "bg-[#37b24d] text-white" : "bg-white border border-[#ced4da]"
            }`}
            aria-label={`${t("Page")} ${totalPages}`}
          >
            {totalPages}
          </button>,
        )
      }
    } else if (currentPage >= totalPages - 3) {
      buttons.push(
        <span
          key="ellipsis"
          className="w-9 h-9 flex rounded text-black items-center justify-center text-base font-normal"
        >
          ...
        </span>,
      )
      const startPage = Math.max(2, totalPages - 4)
      for (let i = startPage; i <= totalPages; i++) {
        buttons.push(
          <button
            key={i}
            onClick={() => onPageChange(i)}
            disabled={loading}
            className={`w-9 h-9 flex rounded text-black items-center justify-center text-base font-normal transition-all duration-300 ${
              currentPage === i ? "bg-[#37b24d] text-white" : "bg-white border border-[#ced4da]"
            }`}
            aria-label={`${t("Page")} ${i}`}
          >
            {i}
          </button>,
        )
      }
    } else {
      buttons.push(
        <span
          key="left-ellipsis"
          className="w-9 h-9 flex rounded text-black items-center justify-center text-base font-normal"
        >
          ...
        </span>,
      )
      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        buttons.push(
          <button
            key={i}
            onClick={() => onPageChange(i)}
            disabled={loading}
            className={`w-9 h-9 flex rounded text-black items-center justify-center text-base font-normal transition-all duration-300 ${
              currentPage === i ? "bg-[#37b24d] text-white" : "bg-white border border-[#ced4da]"
            }`}
            aria-label={`${t("Page")} ${i}`}
          >
            {i}
          </button>,
        )
      }
      buttons.push(
        <span
          key="right-ellipsis"
          className="w-9 h-9 flex rounded text-black items-center justify-center text-base font-normal"
        >
          ...
        </span>,
      )
      buttons.push(
        <button
          key={totalPages}
          onClick={() => onPageChange(totalPages)}
          disabled={loading}
          className={`w-9 h-9 flex rounded text-black items-center justify-center text-base font-normal transition-all duration-300 ${
            currentPage === totalPages ? "bg-[#37b24d] text-white" : "bg-white border border-[#ced4da]"
          }`}
          aria-label={`${t("Page")} ${totalPages}`}
        >
          {totalPages}
        </button>,
      )
    }

    return buttons
  }

  if (totalPages <= 1) return null

  return (
    <div className="flex justify-center">
      <div className="flex items-center gap-2">{renderPaginationButtons()}</div>
    </div>
  )
}

export default LatestNewsPagination
